/**
 * The $ELYXR announcement bar, and the contract pill that sits in the header.
 *
 * Both read `lib/token.ts` and nothing else, so the day the address lands they
 * flip together. Pre-launch they say "launching soon" and carry no chart link,
 * because there is no chart — a dead DexScreener tab is a worse first
 * impression than an honest wait.
 */
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { TOKEN, dexscreenerUrl, isTokenLive, shortContract } from '../lib/token'

/* Dismissal is remembered per state, not globally: whoever closes the
   "launching soon" bar has not dismissed the launch itself, and should still
   be told when it actually goes live. */
const DISMISS_KEY = `elyxr:token-banner:${isTokenLive() ? 'live' : 'soon'}`

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    // Private mode and blocked site data both throw rather than return null.
    return false
  }
}

export function TokenBanner() {
  const [dismissed, setDismissed] = useState(true)

  // Read after mount rather than in the initial state, so the server-rendered
  // and first client paint agree and the bar cannot flash in then vanish.
  useEffect(() => setDismissed(readDismissed()), [])

  if (dismissed) return null

  const live = isTokenLive()
  const chart = dexscreenerUrl()

  const close = () => {
    setDismissed(true)
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {
      // Not being able to remember it is not a reason to fail to close it.
    }
  }

  return (
    <div
      className="relative border-b"
      style={{
        background: live ? 'var(--color-brand-wash)' : 'var(--color-surface-2)',
        borderColor: live
          ? 'color-mix(in srgb, var(--color-brand) 40%, var(--color-line))'
          : 'var(--color-line)',
      }}
    >
      <div className="flex w-full items-center justify-center gap-x-3 gap-y-1 px-10 py-2 text-xs sm:flex-wrap lg:px-12">
        <span
          className="shrink-0 px-1.5 py-0.5 text-[0.5625rem] font-bold tracking-wider uppercase"
          style={{
            background: live ? 'var(--color-brand)' : 'var(--color-surface-3)',
            color: live ? '#101010' : 'var(--color-muted)',
          }}
        >
          {live ? 'Live' : 'Soon'}
        </span>

        <span className="min-w-0 truncate font-semibold">
          {live ? (
            <>
              {TOKEN.ticker} is now live
            </>
          ) : (
            <>
              {TOKEN.ticker} is launching soon
            </>
          )}
        </span>

        {live && chart ? (
          <a
            href={chart}
            target="_blank"
            rel="noreferrer noopener"
            className="shrink-0 font-semibold underline"
            style={{ color: 'var(--color-brand)' }}
          >
            View on DexScreener
          </a>
        ) : (
          <Link
            to="/token"
            className="shrink-0 font-semibold underline"
            style={{ color: 'var(--color-muted)' }}
          >
            How the flywheel works
          </Link>
        )}
      </div>

      <button
        onClick={close}
        aria-label="Dismiss announcement"
        className="absolute inset-y-0 right-2 px-2 text-base leading-none lg:right-4"
        style={{ color: 'var(--color-faint)' }}
      >
        ×
      </button>
    </div>
  )
}

/**
 * The contract address in the header.
 *
 * Pre-launch this is a label, not a control: there is nothing to copy, so it
 * does not pretend to be a button. Once the address exists it becomes one,
 * because copying the CA is the single most common thing anyone will want
 * from this bar.
 */
export function ContractPill() {
  const [copied, setCopied] = useState(false)
  const live = isTokenLive()
  const short = shortContract()

  useEffect(() => {
    if (!copied) return
    const id = window.setTimeout(() => setCopied(false), 1600)
    return () => window.clearTimeout(id)
  }, [copied])

  if (!live || !short) {
    return (
      <span
        className="hidden items-center gap-1.5 border px-2 py-1 text-[0.6875rem] font-medium md:inline-flex"
        style={{ borderColor: 'var(--color-line)', color: 'var(--color-faint)' }}
        title={`${TOKEN.ticker} has not launched yet`}
      >
        <span className="font-bold" style={{ color: 'var(--color-muted)' }}>
          {TOKEN.ticker}
        </span>
        Launching soon
      </span>
    )
  }

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(TOKEN.contract!)
      setCopied(true)
    } catch {
      // Clipboard is blocked in some contexts; the address is on screen anyway.
    }
  }

  return (
    <button
      onClick={() => void copy()}
      aria-label={`Copy the ${TOKEN.ticker} contract address`}
      className="hash hidden items-center gap-1.5 border px-2 py-1 text-[0.6875rem] transition-colors md:inline-flex"
      style={{
        borderColor: 'color-mix(in srgb, var(--color-brand) 35%, var(--color-line))',
        color: 'var(--color-muted)',
      }}
    >
      <span className="font-bold" style={{ color: 'var(--color-brand)' }}>
        {TOKEN.ticker}
      </span>
      <span>{copied ? 'Copied' : short}</span>
    </button>
  )
}
